
import React, { useState, useEffect } from 'react';
import { X, Copy, Gift, Check, Sparkles, PartyPopper, Share2, User, Loader2, Crown } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { get } from '../services/api';
import { copyToClipboard } from '../utils/clipboard';

interface InviteModalProps {
  onClose: () => void;
}

interface InviteInfo {
  inviteCode: string;
  inviteLink: string;
  invitedCount: number;
}

export default function InviteModal({ onClose }: InviteModalProps) {
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const { t, language } = useLanguage();

  // 获取邀请信息
  useEffect(() => {
    const fetchInviteInfo = async () => {
      try {
        const res = await get<InviteInfo>('/invitation/my-invite-info');
        if (res.data) {
          setInviteInfo(res.data);
        }
      } catch (error) {
        console.error('获取邀请信息失败:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInviteInfo();
  }, []);

  const inviteCode = inviteInfo?.inviteCode || '';
  const inviteLink = inviteInfo ? `${window.location.origin}${inviteInfo.inviteLink}` : '';
  const invitedCount = inviteInfo?.invitedCount || 0;

  // 兼容 HTTP 的复制方法（内网穿透等非 HTTPS 场景）
  const handleCopyCode = () => {
    if (copyToClipboard(inviteCode)) {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleCopyLink = () => {
    if (copyToClipboard(inviteLink)) {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-sans text-brand-900">
      {/* Backdrop - Black/40 */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal - Vibrant & Dynamic - UPDATED TO AMBER/ORANGE THEME */}
      <div className="relative w-full max-w-md bg-surface-light rounded-modal shadow-2xl p-0 animate-in zoom-in-95 fade-in slide-in-from-bottom-8 duration-300 border border-surface-border overflow-hidden group">
        
        {/* Background Decorative Blobs - Orange/Amber */}
        <div className="absolute -top-20 -left-20 w-60 h-60 bg-orange-200/30 rounded-full blur-[60px] animate-pulse"></div>
        <div className="absolute -bottom-20 -right-20 w-60 h-60 bg-orange-100/40 rounded-full blur-[60px]"></div>

        {/* Dynamic Confetti/Sparkles Overlay */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
             {/* Random floating shapes - Orange/Gold */}
             <div className="absolute top-10 left-10 text-orange-300 animate-[spin_4s_linear_infinite]"><Sparkles size={16} /></div>
             <div className="absolute top-20 right-10 text-orange-200 animate-[bounce_3s_infinite]"><div className="w-2 h-2 rounded-full bg-current"></div></div>
             <div className="absolute bottom-32 left-8 text-orange-500 animate-[pulse_2s_infinite]"><div className="w-3 h-3 rounded-full border-2 border-current"></div></div>
        </div>

        {/* Close Button */}
        <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-ink-muted hover:text-brand-900 rounded-full hover:bg-surface transition-colors z-20"
        >
            <X size={20} />
        </button>

        {/* Header Content */}
        <div className="pt-12 pb-8 px-8 text-center relative z-10">
            {/* Animated Icon Container */}
            <div className="w-24 h-24 mx-auto mb-6 relative">
                {/* Glow Ring - Amber */}
                <div className="absolute inset-0 bg-orange-400 rounded-3xl blur-xl opacity-40 animate-pulse"></div>
                
                {/* Main Icon Box - Orange Gradient */}
                <div className="relative w-full h-full bg-gradient-to-br from-orange-100 to-orange-300 rounded-3xl flex items-center justify-center shadow-lg transform transition-transform hover:scale-110 duration-300 border-2 border-white rotate-3 hover:rotate-6">
                    <Gift size={48} className="text-orange-700 animate-[wiggle_1s_ease-in-out_infinite]" strokeWidth={2} />
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-2 -right-2 bg-white rounded-full p-1.5 shadow-md border border-orange-300 animate-[bounce_2s_infinite]">
                    <PartyPopper size={16} className="text-orange-600" />
                </div>
            </div>

            <h2 className="text-3xl font-serif font-bold text-brand-900 mb-3 tracking-tight">{t.inviteModal.title}</h2>
            <p className="text-ink-secondary leading-relaxed text-sm max-w-[280px] mx-auto">
                {language === 'zh'
                  ? '邀请好友注册，双方各得60分钟时长！'
                  : 'Invite friends to register, both get 60 min!'}
            </p>
        </div>

        {/* Action Area */}
        <div className="px-8 pb-8 relative z-10 space-y-6">

            {/* 时长奖励提示 */}
            <div className="flex items-center justify-center gap-2 py-2 px-4 bg-gradient-to-r from-amber-100 to-amber-200 rounded-xl border border-amber-300">
                <Crown size={16} className="text-amber-600" />
                <span className="text-sm font-bold text-amber-800">
                    {language === 'zh' ? '奖励：双方各得60分钟免费时长' : 'Reward: Both get 60 min free'}
                </span>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <Loader2 size={24} className="animate-spin text-orange-400" />
                </div>
            ) : (
            <>
            {/* Ticket/Coupon Code Style - Amber Theme */}
            <div className="bg-orange-50 rounded-2xl p-1 border-2 border-dashed border-orange-300 relative group/ticket overflow-hidden">
                <div className="absolute inset-0 bg-orange-100/30 opacity-0 group-hover/ticket:opacity-100 transition-opacity"></div>
                
                <div className="bg-white rounded-xl p-4 text-center shadow-sm relative z-10">
                    <p className="text-[10px] font-bold text-orange-600 uppercase tracking-widest mb-1">{t.inviteModal.yourCode}</p>
                    <div 
                        className="text-3xl font-mono font-bold text-brand-900 tracking-widest cursor-pointer select-all mb-3 hover:scale-105 transition-transform"
                        onClick={handleCopyCode}
                    >
                        {inviteCode}
                    </div>
                    <button 
                        onClick={handleCopyCode}
                        className={`w-full h-9 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all
                        ${copiedCode 
                            ? 'bg-brand-900 text-white shadow-md' 
                            : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}
                    >
                        {copiedCode ? <Check size={14} /> : <Copy size={14} />}
                        {copiedCode ? t.inviteModal.copied : t.inviteModal.tapToCopy}
                    </button>
                </div>
            </div>

            {/* Link Section */}
            <div>
                <div className="flex items-center gap-2 mb-2">
                    <div className="h-px bg-surface-border flex-1"></div>
                    <span className="text-[10px] font-bold text-ink-muted uppercase tracking-wider">{t.inviteModal.orShareLink}</span>
                    <div className="h-px bg-surface-border flex-1"></div>
                </div>
                
                <div className="flex gap-2">
                    <input 
                        type="text" 
                        readOnly 
                        value={inviteLink}
                        className="flex-1 h-11 px-4 rounded-xl border border-surface-border bg-surface text-sm text-ink-dark focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all font-medium"
                    />
                    <button 
                        onClick={handleCopyLink}
                        className="h-11 w-11 bg-white border border-surface-border text-brand-900 rounded-xl hover:bg-orange-50 hover:border-orange-400 hover:text-orange-600 transition-colors flex items-center justify-center shadow-sm"
                    >
                        {copiedLink ? <Check size={18} /> : <Share2 size={18} />}
                    </button>
                </div>
            </div>
            </>
            )}
        </div>

        {/* Footer */}
        <div className="bg-orange-50 py-4 px-8 border-t border-orange-200 flex justify-between items-center text-[10px] text-orange-700 font-medium relative z-10">
            <span className="hover:underline cursor-pointer">{t.inviteModal.terms}</span>
            <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded-md border border-orange-200 shadow-sm">
                <User size={10} />
                <span>{language === 'zh' ? `已邀请 ${invitedCount} 人` : `${invitedCount} Referrals`}</span>
            </div>
        </div>

        {/* Custom Keyframe Styles */}
        <style>{`
            @keyframes wiggle {
                0%, 100% { transform: rotate(-3deg); }
                50% { transform: rotate(3deg); }
            }
        `}</style>

      </div>
    </div>
  );
}
